import { useState } from 'react';
import {
  CalendarDays,
  Pencil,
  Trash2,
  Play,
  Pause,
  RefreshCw,
  ShieldCheck,
  Ticket,
  Users,
  Mail,
  ShieldAlert,
  Key,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import toast from 'react-hot-toast';
import {
  ReportDefinition,
  ReportType,
  REPORT_TYPE_INFO,
  SCHEDULE_FREQUENCY_INFO,
  DAYS_OF_WEEK,
  Client,
} from '../../types';
import { useDeleteReportDefinition, useUpdateReportDefinition, useRunReport } from '../../hooks/useScheduledReports';

interface ReportDefinitionListProps {
  definitions: ReportDefinition[];
  loading: boolean;
  error: string | null;
  onEdit: (id: string) => void;
  onDelete: () => void;
  onRefresh: () => void;
  onReportRun?: () => void;
  // Filter props
  clients: Client[];
  clientFilter: string;
  setClientFilter: (v: string) => void;
  reportTypeFilter: string;
  setReportTypeFilter: (v: string) => void;
  enabledFilter: string;
  setEnabledFilter: (v: string) => void;
}

const REPORT_ICONS: Record<ReportType, typeof ShieldCheck> = {
  'mfa': ShieldCheck,
  'license': Ticket,
  'admin-roles': Users,
  'mailbox-security': Mail,
  'email-security': ShieldAlert,
  'conditional-access': Key,
};

const STATUS_COLORS: Record<string, string> = {
  'completed': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  'running': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  'queued': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
  'failed': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
};

export default function ReportDefinitionList({
  definitions,
  loading,
  error,
  onEdit,
  onDelete,
  onRefresh,
  onReportRun,
  clients,
  clientFilter,
  setClientFilter,
  reportTypeFilter,
  setReportTypeFilter,
  enabledFilter,
  setEnabledFilter,
}: ReportDefinitionListProps) {
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [runningId, setRunningId] = useState<string | null>(null);
  const { deleteDefinition, loading: deleting } = useDeleteReportDefinition();
  const { updateDefinition, loading: updating } = useUpdateReportDefinition();
  const { runReport, loading: running } = useRunReport();

  const getScheduleDescription = (def: ReportDefinition): string => {
    const { schedule } = def;
    if (!schedule.enabled) return 'Disabled';
    if (schedule.frequency === 'manual') return 'Manual only';

    let desc = SCHEDULE_FREQUENCY_INFO[schedule.frequency];
    if (schedule.frequency === 'weekly' && schedule.dayOfWeek !== undefined) {
      desc += ` ${DAYS_OF_WEEK[schedule.dayOfWeek]}`;
    }
    if (schedule.frequency === 'monthly' && schedule.dayOfMonth !== undefined) {
      desc += ` day ${schedule.dayOfMonth}`;
    }
    if (schedule.time) {
      desc += ` ${schedule.time}`;
    }
    return desc;
  };

  const handleDelete = async (id: string, clientName: string) => {
    if (!confirm(`Delete scheduled report for ${clientName}?`)) return;

    setDeletingId(id);
    try {
      await deleteDefinition(id);
      toast.success('Report definition deleted');
      onDelete();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeletingId(null);
    }
  };

  const handleToggleEnabled = async (def: ReportDefinition) => {
    try {
      await updateDefinition({
        id: def.id,
        data: { schedule: { enabled: !def.schedule.enabled } }
      });
      toast.success(def.schedule.enabled ? 'Schedule disabled' : 'Schedule enabled');
      onRefresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to update');
    }
  };

  const handleRunNow = async (def: ReportDefinition) => {
    setRunningId(def.id);
    const toastId = toast.loading(`Generating ${REPORT_TYPE_INFO[def.reportType].name} for ${def.clientName}...`);

    try {
      const result = await runReport({
        definitionId: def.id,
        triggeredByUser: 'Manual run from UI'
      });

      if (result.success) {
        toast.success('Report generated successfully!', { id: toastId });
        if (onReportRun) {
          onReportRun();
        } else {
          onRefresh();
        }
      } else {
        toast.error(result.error || 'Report generation failed', { id: toastId });
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to run report', { id: toastId });
    } finally {
      setRunningId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card p-4 bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300">
        {error}
      </div>
    );
  }

  if (definitions.length === 0 && !clientFilter && !reportTypeFilter && !enabledFilter) {
    return (
      <div className="card p-12 text-center">
        <CalendarDays className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
        <h3 className="text-lg font-medium text-foreground">
          No scheduled reports
        </h3>
        <p className="text-muted-foreground mt-2">
          Create your first scheduled report to start generating automated security dashboards.
        </p>
      </div>
    );
  }

  const filterSelectClass = "w-full px-2 py-1 text-xs border-0 bg-transparent text-muted-foreground focus:ring-0 cursor-pointer [&>option]:bg-card [&>option]:text-foreground";

  return (
    <div className="space-y-2">
      {/* Header bar */}
      <div className="flex items-center justify-between h-10">
        <span className="text-sm text-muted-foreground">
          {definitions.length} report{definitions.length !== 1 ? 's' : ''} configured
        </span>
        <button
          onClick={onRefresh}
          className="btn-secondary text-sm"
        >
          <RefreshCw className="h-4 w-4 mr-1" />
          Refresh
        </button>
      </div>

      {/* Definition table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-border">
            <thead className="bg-muted">
              {/* Column headers */}
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Client
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Report
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Schedule
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Last Run
                </th>
                <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Options
                </th>
                <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Actions
                </th>
              </tr>
              {/* Filter row */}
              <tr className="bg-muted">
                <th className="px-1 py-1">
                  <select
                    value={clientFilter}
                    onChange={(e) => setClientFilter(e.target.value)}
                    className={filterSelectClass}
                  >
                    <option value="">All Clients</option>
                    {clients.map(c => (
                      <option key={c.id} value={c.id}>{c.companyName}</option>
                    ))}
                  </select>
                </th>
                <th className="px-1 py-1">
                  <select
                    value={reportTypeFilter}
                    onChange={(e) => setReportTypeFilter(e.target.value)}
                    className={filterSelectClass}
                  >
                    <option value="">All Types</option>
                    {Object.entries(REPORT_TYPE_INFO).map(([key, info]) => (
                      <option key={key} value={key}>{info.name}</option>
                    ))}
                  </select>
                </th>
                <th className="px-1 py-1">
                  <select
                    value={enabledFilter}
                    onChange={(e) => setEnabledFilter(e.target.value)}
                    className={filterSelectClass}
                  >
                    <option value="">All</option>
                    <option value="true">Enabled</option>
                    <option value="false">Disabled</option>
                  </select>
                </th>
                <th className="px-1 py-1"></th>
                <th className="px-1 py-1"></th>
                <th className="px-1 py-1"></th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-border">
              {definitions.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    No reports match your filters
                  </td>
                </tr>
              ) : (
                definitions.map((def) => {
                  const Icon = REPORT_ICONS[def.reportType];
                  const reportInfo = REPORT_TYPE_INFO[def.reportType];
                  const isRunning = running && runningId === def.id;
                  const isDeleting = deleting && deletingId === def.id;

                  return (
                    <tr key={def.id} className="hover:bg-accent">
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="p-1.5 rounded-md bg-primary-100 dark:bg-primary-900/30">
                            <Icon className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                          </div>
                          <span className="font-medium text-foreground text-sm">
                            {def.clientName}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <span className="text-sm text-muted-foreground">
                          {reportInfo.name}
                        </span>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <span className={`text-sm ${def.schedule.enabled ? 'text-muted-foreground' : 'text-muted-foreground'}`}>
                            {getScheduleDescription(def)}
                          </span>
                          {!def.schedule.enabled && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-muted text-muted-foreground">
                              Off
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        {def.lastRunAt ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-muted-foreground">
                              {formatDistanceToNow(new Date(def.lastRunAt), { addSuffix: true })}
                            </span>
                            {def.lastRunStatus && (
                              <span className={`px-1.5 py-0.5 rounded text-xs ${STATUS_COLORS[def.lastRunStatus] || 'bg-muted text-muted-foreground'}`}>
                                {def.lastRunStatus}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">Never</span>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          {def.options.uploadToITGlue && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400">
                              ITG
                            </span>
                          )}
                          {def.options.emailOnComplete && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400">
                              Email
                            </span>
                          )}
                          {!def.options.uploadToITGlue && !def.options.emailOnComplete && (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => handleRunNow(def)}
                            disabled={isRunning}
                            className={`px-2 py-1 text-xs font-medium rounded transition-colors ${
                              isRunning
                                ? 'bg-muted text-muted-foreground cursor-not-allowed'
                                : 'bg-primary-600 text-white hover:bg-primary-700'
                            }`}
                            title="Run report now"
                          >
                            {isRunning ? (
                              <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <Play className="h-3.5 w-3.5" />
                            )}
                          </button>
                          <button
                            onClick={() => handleToggleEnabled(def)}
                            disabled={updating}
                            className="p-1.5 text-muted-foreground hover:text-foreground transition-colors"
                            title={def.schedule.enabled ? 'Disable schedule' : 'Enable schedule'}
                          >
                            {def.schedule.enabled ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </button>
                          <button
                            onClick={() => onEdit(def.id)}
                            className="p-1.5 text-muted-foreground hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(def.id, def.clientName)}
                            disabled={isDeleting}
                            className="p-1.5 text-muted-foreground hover:text-red-600 dark:hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
